import { connect, ConnectedProps } from 'react-redux';
import classNames from 'classnames';

import { OrderSideEnum } from '../../../../../../lib/enums';
import { RootState } from '../../../../../../lib/store';
import { ChartDataRange, ChartDataRangeEnum } from '../../../../../../lib/store/market-data/charting/types';
import BigChartCache from '../../../../../../lib/store-util/BigChartCache';
import ChartHoverCache from '../../../../../../lib/store-util/ChartHoverCache';
import SymbolStatusCache from '../../../../../../lib/store-util/SymbolStatusCache';
import { CalculatedSymbolPrices } from '../../../../../../lib/util/ChartingHelpers';
import { calculateDirectionFromChange } from '../../../../../../lib/util/DataChangeHelpers';
import { formatCurrency } from '../../../../../../lib/util/DataHelpers';
import { checkIfSymbolIsHalted } from '../../../../../../lib/util/HaltSymbolHelper';
import { useMarketStateInfo } from '../../../../../../lib/util/hooks/market/MarketStateInfoHook';

import { useIsMobile, useTradingButtonsDisabled } from '../../../../../../hooks';
import TradingButtons from '../../../../../TradingButtons/TradingButtons';
import { getChangeStyle } from '../../../../getChangeStyle';
import { priceChange } from '../../helpers';
import ExtraPriceInfo from '../ExtraPriceInfo/ExtraPriceInfo';
import InfoStatus from '../InfoStatus/InfoStatus';

import styles from './MainPriceInfo.module.scss';

type OwnProps = {
  symbol: string,
  currentRange: ChartDataRange,
  handleCreateOrderSellOrBuy: (target: string, isHeaderBtn: boolean) => void;
};
type Props = OwnProps & CalculatedSymbolPrices & ReduxProps;

const PERCENT_MULTIPLIER = 100;

const MainPriceInfo = (props: Props): JSX.Element => {
  const {
    currentMainPrice,
    changeMainPrice,
    changedPercentMainPrice,
    mainPriceDirection,
    hasAfterMarketData,
    symbol,
    currentRange,
    handleCreateOrderSellOrBuy,
    accountStatusData,
  } = props;
  const { isMobile, isTablet } = useIsMobile();
  const symbolStatus = SymbolStatusCache.use(symbol);

  const isButtonsDisabled = useTradingButtonsDisabled({ symbol, accountStatusData });
  // The halted notice comes from SymbolDetailsHeader on these screens, so only hide the buttons here
  const isHaltedSymbol = checkIfSymbolIsHalted(symbolStatus);

  // The tooltip carries the hovered values now, so the header only reacts to hovering by
  // getting out of its way - the price and change stay on the live quote
  const { isHovering } = ChartHoverCache.use();
  const { dataByRange } = BigChartCache.use();
  const firstClose = dataByRange?.[currentRange]?.lineData?.[0]?.close;

  const displayPrice = currentMainPrice;

  let displayChange = changeMainPrice;
  let displayPercent = changedPercentMainPrice;
  let displayDirection = mainPriceDirection;

  if (currentRange !== ChartDataRangeEnum.OneDay && firstClose && displayPrice != null) {
    displayChange = displayPrice - firstClose;
    displayPercent = (displayChange / firstClose) * PERCENT_MULTIPLIER;
    displayDirection = calculateDirectionFromChange(displayChange);
  }

  const noChangeData = displayPercent == null;
  const changeStyle = getChangeStyle(displayDirection);

  const priceChangeStyle = classNames(
    styles.mainPriceChange,
    { [styles.mainPriceChangeHidden]: noChangeData },
  );
  const { status: infoType } = useMarketStateInfo(!!hasAfterMarketData);
  // Phone and tablet stack the price over the change; only the desktop header keeps them on one line
  const isCompactLayout = isMobile || isTablet;
  const isAfterOrClose = infoType === 'after' || infoType === 'close';

  const handleClickButtonSellOrBuy = (target: string) => {
    handleCreateOrderSellOrBuy(target, true);
  };

  return (
    <>
      <div className={isMobile ? styles.wrapperPriceHeaderMobile : styles.wrapperPriceHeader}>
        {displayPrice ? (
          <div className={classNames(styles.priceInfo, {
            [styles.priceInfoAfterAndCloseMobile]: isAfterOrClose && isCompactLayout,
          })}
          >
            {isCompactLayout ? (
              <>
                <div className={styles.mobilePriceAndVolumeRow}>
                  <span className={styles.mainPriceMobile}>
                    {formatCurrency(displayPrice)}
                  </span>
                </div>
                <div className={styles.mobilePriceChangeRow}>
                  <span className={priceChangeStyle} style={changeStyle}>
                    {priceChange(displayChange, displayPercent)}
                  </span>
                  <InfoStatus type={infoType} />
                  <div className={isHovering ? styles.extraPriceInfoHoverHidden : undefined}>
                    <ExtraPriceInfo {...props} />
                  </div>
                </div>
              </>
            ) : (
              <>
                <span className={styles.mainPrice}>
                  {formatCurrency(displayPrice)}
                </span>
                <span className={priceChangeStyle} style={changeStyle}>
                  {priceChange(displayChange, displayPercent)}
                </span>
                <InfoStatus type={infoType} />
                {!isHovering && <ExtraPriceInfo {...props} />}
              </>
            )}
          </div>
        ) : (
          <div className={styles.loadingArea}>
            <div className={styles.priceLoader} />
            <div className={styles.priceLoader} />
          </div>
        )}
        {
          isTablet
          && !isHaltedSymbol
          && (
            <TradingButtons
              isButtonsDisabled={isButtonsDisabled}
              onSellClicked={() => handleClickButtonSellOrBuy(OrderSideEnum.Sell)}
              onBuyClicked={() => handleClickButtonSellOrBuy(OrderSideEnum.Buy)}
            />
          )
        }
      </div>
    </>
  );
};
const mapStateToProps = (state: RootState) => ({
  accountStatusData: state.closeout.accountStatusData,
});

const mapDispatchToProps = () => ({
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(MainPriceInfo);
